import {
  MappingWebSocketMessageType,
  type IMappingNotificationMessage,
} from "@models/mapping";

const BASE_URL = import.meta.env.VITE_MAPPING_NOTIFICATION_URL;

/**
 * Org-broadcast WebSocket client for the xplatform mapping service.
 *
 * Mirrors the mapping frontend's notifier, but the extraction FE does not
 * know mapping job_ids (they are generated async server-side), so incoming
 * `mapping.job.{progress,completed,failed}` frames are correlated to the
 * extraction result they belong to by `process_item_id`. One socket per
 * browser session, authenticated with the BeeHive JWT, dispatched to
 * per-process-item subscribers.
 */

type SubscriberCallback = (message: IMappingNotificationMessage) => void;

const HEARTBEAT_INTERVAL_MS = 5 * 60 * 1000; // API GW idle timeout is 10min

class MappingNotificationApi {
  private websocket: WebSocket | null = null;
  private isAuthenticated = false;
  private heartbeatInterval: number | null = null;
  private connectPromise: Promise<void> | null = null;
  private subscribers: Map<string, Set<SubscriberCallback>> = new Map();

  private getAccessToken(): string | null {
    try {
      return localStorage.getItem("accessToken");
    } catch (error) {
      console.error("Error reading access token from localStorage:", error);
      return null;
    }
  }

  /**
   * Open the WS and authenticate. Idempotent — repeated calls return the
   * same in-flight promise. Resolves once the auth frame has been sent.
   */
  public connect(): Promise<void> {
    if (this.isAuthenticated && this.websocket?.readyState === WebSocket.OPEN) {
      return Promise.resolve();
    }
    if (this.connectPromise) {
      return this.connectPromise;
    }
    if (!BASE_URL) {
      return Promise.reject(
        new Error(
          "VITE_MAPPING_NOTIFICATION_URL is not set. Cannot open mapping status WebSocket.",
        ),
      );
    }
    const token = this.getAccessToken();
    if (!token) {
      return Promise.reject(
        new Error("No access token available for WebSocket authentication."),
      );
    }

    this.connectPromise = new Promise<void>((resolve, reject) => {
      const socket = new WebSocket(BASE_URL);
      this.websocket = socket;

      socket.onopen = () => {
        socket.send(
          JSON.stringify({
            type: MappingWebSocketMessageType.Authenticate,
            token,
          }),
        );
        this.isAuthenticated = true;
        this.startHeartbeat();
        resolve();
      };

      socket.onmessage = (event) => {
        this.handleMessage(event);
      };

      socket.onerror = (error) => {
        console.error("Mapping notification WebSocket error:", error);
        if (!this.isAuthenticated) {
          reject(new Error("WebSocket connection error during handshake."));
        }
      };

      socket.onclose = () => {
        this.cleanup();
      };
    }).finally(() => {
      this.connectPromise = null;
    });

    return this.connectPromise;
  }

  /**
   * Subscribe to events for a specific process_item_id. Returns an
   * unsubscribe fn. Multiple subscribers per item are allowed.
   */
  public subscribe(
    processItemId: string,
    callback: SubscriberCallback,
  ): () => void {
    const set =
      this.subscribers.get(processItemId) ?? new Set<SubscriberCallback>();
    set.add(callback);
    this.subscribers.set(processItemId, set);
    return () => {
      const current = this.subscribers.get(processItemId);
      if (!current) return;
      current.delete(callback);
      if (current.size === 0) {
        this.subscribers.delete(processItemId);
      }
    };
  }

  private handleMessage(event: MessageEvent): void {
    let parsed: IMappingNotificationMessage;
    try {
      parsed = JSON.parse(event.data) as IMappingNotificationMessage;
    } catch (error) {
      console.error("Failed to parse mapping WS message:", error, event.data);
      return;
    }
    if (
      parsed.type !== MappingWebSocketMessageType.Progress &&
      parsed.type !== MappingWebSocketMessageType.Completed &&
      parsed.type !== MappingWebSocketMessageType.Failed
    ) {
      return;
    }
    if (!parsed.process_item_id) {
      return;
    }
    const callbacks = this.subscribers.get(parsed.process_item_id);
    if (!callbacks) return;
    for (const callback of callbacks) {
      try {
        callback(parsed);
      } catch (error) {
        console.error("Mapping subscriber callback threw:", error);
      }
    }
  }

  private startHeartbeat(): void {
    this.stopHeartbeat();
    this.heartbeatInterval = window.setInterval(() => {
      if (this.websocket?.readyState === WebSocket.OPEN) {
        this.websocket.send(
          JSON.stringify({ type: MappingWebSocketMessageType.Ping }),
        );
      }
    }, HEARTBEAT_INTERVAL_MS);
  }

  private stopHeartbeat(): void {
    if (this.heartbeatInterval !== null) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  private cleanup(): void {
    this.stopHeartbeat();
    this.isAuthenticated = false;
    this.websocket = null;
  }

  public disconnect(): void {
    this.subscribers.clear();
    this.stopHeartbeat();
    const socket = this.websocket;
    this.websocket = null;
    this.isAuthenticated = false;
    if (socket && socket.readyState < WebSocket.CLOSING) {
      socket.close();
    }
  }
}

export const mappingNotificationApi = new MappingNotificationApi();
